import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import {
    alpha,
    Badge,
    Box,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Popover,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import ServiceAPI from '../../services/serviceapi';


interface Notificacao {
    codigo: number;
    tipo: string;
    titulo: string;
    mensagem: string;
    lida: boolean;
    dhCadastro: string;
}

const POLLING_MS = 120_000;

function tocarSom() {
    try {
        const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(880, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.15);
        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.3);
    } catch {
    }
}

function formatarData(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function NotificacaoMenu() {
    const theme = useTheme();
    const [anchorEl, setAnchorEl]           = useState<null | HTMLElement>(null);
    const [notificacoes, setNotificacoes]   = useState<Notificacao[]>([]);
    const [naoLidas, setNaoLidas]           = useState(0);
    const intervalRef                        = useRef<ReturnType<typeof setInterval> | null>(null);
    const primeiraVezRef                     = useRef(true);
    const naoLidasRef                        = useRef(0);

    const carregar = useCallback(async () => {
        try {
            const resposta = await ServiceAPI.execNativeService({
                url: '/notificacao/listar',
                data: {},
                config: { ignoreToast: true },
            });
            const lista: Notificacao[] = Array.isArray(resposta) ? resposta : (resposta?.dataResponse ?? []);
            const qtdNaoLidas = lista.filter(n => !n.lida).length;

            if (!primeiraVezRef.current && qtdNaoLidas > naoLidasRef.current) {
                tocarSom();
            }

            primeiraVezRef.current = false;
            naoLidasRef.current    = qtdNaoLidas;

            setNotificacoes(lista);
            setNaoLidas(qtdNaoLidas);
        } catch {
        }
    }, []);

    useEffect(() => {
        carregar();
        intervalRef.current = setInterval(carregar, POLLING_MS);
        return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }, [carregar]);

    const abrirPopover = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
    const fecharPopover = () => setAnchorEl(null);

    const marcarLida = async (codigo: number) => {
        try {
            await ServiceAPI.execNativeService({
                url: '/notificacao/marcarlida',
                data: { codigo },
                config: { ignoreToast: true },
            });
            setNotificacoes(prev => prev.map(n => n.codigo === codigo ? { ...n, lida: true } : n));
            setNaoLidas(prev => Math.max(0, prev - 1));
        } catch {
        }
    };

    const marcarTodasLidas = async () => {
        try {
            await ServiceAPI.execNativeService({
                url: '/notificacao/marcartodaslidas',
                data: {},
                config: { ignoreToast: true },
            });
            setNotificacoes(prev => prev.map(n => ({ ...n, lida: true })));
            setNaoLidas(0);
        } catch {
        }
    };

    const aberto = Boolean(anchorEl);

    return (
        <>
            <Tooltip title="Notificações">
                <IconButton size="small" onClick={abrirPopover}>
                    <Badge badgeContent={naoLidas} color="error" max={99}>
                        {naoLidas > 0
                            ? <NotificationsIcon fontSize="small" />
                            : <NotificationsNoneIcon fontSize="small" />
                        }
                    </Badge>
                </IconButton>
            </Tooltip>

            <Popover
                open={aberto}
                anchorEl={anchorEl}
                onClose={fecharPopover}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        width: 360,
                        maxHeight: 480,
                        borderRadius: 3,
                        mt: 0.5,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }
                }}
            >
                <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                        Notificações {naoLidas > 0 && `(${naoLidas} não lidas)`}
                    </Typography>
                    {naoLidas > 0 && (
                        <Tooltip title="Marcar todas como lidas">
                            <IconButton size="small" onClick={marcarTodasLidas}>
                                <DoneAllIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>

                <Divider />

                <List sx={{ overflowY: 'auto', flexGrow: 1, py: 0 }}>
                    {notificacoes.length === 0 && (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <NotificationsNoneIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                                Nenhuma notificação
                            </Typography>
                        </Box>
                    )}

                    {notificacoes.map((n) => (
                        <ListItem
                            key={n.codigo}
                            alignItems="flex-start"
                            onClick={() => !n.lida && marcarLida(n.codigo)}
                            sx={{
                                cursor: n.lida ? 'default' : 'pointer',
                                bgcolor: n.lida ? 'transparent' : alpha(theme.palette.primary.main, 0.06),
                                borderLeft: n.lida ? '3px solid transparent' : `3px solid ${theme.palette.primary.main}`,
                                transition: 'background 0.2s',
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.action.hover, 0.08),
                                },
                                py: 1.5,
                            }}
                        >
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                        <Typography variant="body2" fontWeight={n.lida ? 400 : 700} sx={{ lineHeight: 1.3 }}>
                                            {n.titulo}
                                        </Typography>
                                        <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                                            {formatarData(n.dhCadastro)}
                                        </Typography>
                                    </Box>
                                }
                                secondary={
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.3, display: 'block' }}>
                                        {n.mensagem}
                                    </Typography>
                                }
                            />
                        </ListItem>
                    ))}
                </List>
            </Popover>
        </>
    );
}

export default NotificacaoMenu;